import React, {useCallback, useEffect, useMemo, useRef} from 'react';
import echarts from '@/utils/echartsCore';
import {DataLabelPosition} from 'entities/chart';
import {
    buildSunburstValueLabelFormatter,
    getSunburstLabelPosition,
} from '@/views/storyboard/utils/chartDataLabels';
import {buildChartImageDownloadToolboxFeatures} from '@/views/storyboard/utils/chartImageDownload';
import {buildSunburstLevels, sunburstLabelFormatter, SunburstTreeNode} from '../utils/aggregationsToSunburstTree';
import {formatHierarchyChartTooltip, HierarchyTooltipParams} from '../utils/hierarchyChartTooltip';

export interface SunburstChartProps {
    data: SunburstTreeNode[];
    levelCount?: number;
    height?: number;
    width?: number;
    chartSettingsView?: boolean;
    title?: string;
    dataLabelPosition?: DataLabelPosition;
    description?: string;
    datePeriod?: string;
}

const SunburstChart: React.FC<SunburstChartProps> = (
    {
        data,
        levelCount = 0,
        height = 1,
        width = 1,
        chartSettingsView = false,
        title = '',
        dataLabelPosition = DataLabelPosition.Off,
        description = '',
        datePeriod = '',
    },
) => {
    const chartRef = useRef<HTMLDivElement>(null);

    const showToolbox = !chartSettingsView && height !== 1;

    const hasHierarchy = useMemo(
        () => data.some((node) => 'children' in node && node.children.length > 0),
        [data],
    );

    const {radius, fontSize} = useMemo(() => {
        let nextRadius: [string, string] = ['15%', '80%'];
        let nextFontSize = 13;

        if (chartSettingsView) {
            nextRadius = ['10%', '65%'];
            nextFontSize = 11;
        } else if (width === 1) {
            nextRadius = ['10%', '70%'];
            nextFontSize = 11;
        }

        return {radius: nextRadius, fontSize: nextFontSize};
    }, [height, width, chartSettingsView]);

    const sunburstLevels = useMemo(() => {
        if (!hasHierarchy || levelCount === 0) {
            return undefined;
        }

        return buildSunburstLevels(levelCount, radius, fontSize, dataLabelPosition);
    }, [hasHierarchy, levelCount, radius, fontSize, dataLabelPosition]);

    const sunburstLabelConfig = useMemo(() => ({
        show: true,
        minAngle: 8,
        rotate: hasHierarchy ? 'tangential' as const : 'radial' as const,
        fontSize,
        color: '#ffffff',
        fontFamily: '"Rubik", sans-serif',
        fontWeight: 500,
        textBorderWidth: 1,
        textBorderColor: 'rgba(0, 0, 0, 0.35)',
        textShadowBlur: 2,
        textShadowColor: 'rgba(0, 0, 0, 0.45)',
        formatter: buildSunburstValueLabelFormatter(dataLabelPosition, sunburstLabelFormatter),
        ...(getSunburstLabelPosition(dataLabelPosition)
            ? {position: getSunburstLabelPosition(dataLabelPosition)}
            : {}),
    }), [hasHierarchy, fontSize, dataLabelPosition]);

    const customTooltip = useCallback(
        (params: HierarchyTooltipParams) => formatHierarchyChartTooltip(params),
        [],
    );

    useEffect(() => {
        if (!chartRef.current) {
            return;
        }

        const chartInstance = echarts.init(chartRef.current);
        const handleResize = () => {
            if (!chartInstance.isDisposed()) {
                chartInstance.resize();
            }
        };

        const handleChatSidebarChange = () => {
            setTimeout(handleResize, 350);
        };

        let resizeObserver: ResizeObserver | null = null;
        if (typeof window !== 'undefined' && 'ResizeObserver' in window && chartRef.current) {
            resizeObserver = new ResizeObserver(() => {
                handleResize();
            });
            resizeObserver.observe(chartRef.current);
        }

        window.addEventListener('resize', handleResize);
        window.addEventListener('chatSidebarOpened', handleChatSidebarChange);
        window.addEventListener('chatSidebarClosed', handleChatSidebarChange);

        return () => {
            window.removeEventListener('resize', handleResize);
            window.removeEventListener('chatSidebarOpened', handleChatSidebarChange);
            window.removeEventListener('chatSidebarClosed', handleChatSidebarChange);
            resizeObserver?.disconnect();
            if (!chartInstance.isDisposed()) {
                chartInstance.dispose();
            }
        };
    }, []);

    useEffect(() => {
        if (!chartRef.current) {
            return;
        }

        const chartInstance = echarts.getInstanceByDom(chartRef.current);
        if (!chartInstance || chartInstance.isDisposed()) {
            return;
        }

        chartInstance.resize();
    }, [height, width]);

    useEffect(() => {
        if (!chartRef.current) {
            return;
        }

        const chartInstance = echarts.getInstanceByDom(chartRef.current);
        if (!chartInstance || chartInstance.isDisposed()) {
            return;
        }

        chartInstance.setOption({
            textStyle: {
                fontFamily: '"Rubik", sans-serif',
                fontSize,
                fontWeight: 400,
            },
            tooltip: {
                trigger: 'item',
                formatter: customTooltip,
            },
            toolbox: {
                show: showToolbox,
                orient: 'vertical',
                itemSize: 12,
                itemGap: 8,
                feature: {
                    ...buildChartImageDownloadToolboxFeatures({
                        title,
                        description,
                        datePeriod,
                        getChartInstance: () => (
                            chartRef.current ? echarts.getInstanceByDom(chartRef.current) : null
                        ),
                    }, showToolbox),
                },
            },
            series: [
                {
                    type: 'sunburst',
                    radius,
                    data,
                    sort: undefined,
                    emphasis: {
                        focus: 'ancestor',
                    },
                    itemStyle: {
                        borderWidth: 1,
                        borderColor: '#ffffff',
                    },
                    label: sunburstLabelConfig,
                    ...(sunburstLevels ? {levels: sunburstLevels} : {}),
                },
            ],
        }, {notMerge: true});
    }, [data, showToolbox, radius, fontSize, title, description, datePeriod, hasHierarchy, sunburstLevels, sunburstLabelConfig, dataLabelPosition, customTooltip]);

    return <div ref={chartRef} style={{width: '100%', height: '100%'}}/>;
};

export default SunburstChart;
